((global) => {
  'use strict';

  // DHL EXPRESS WORLDWIDE EXPORT net rates, China 2026 agreement, zones 1–9.
  // Values through 30 kg are represented as exact piecewise 0.5 kg progressions.
  const HALF_KG_SEGMENTS = Object.freeze([
    Object.freeze({
      from: 0.5,
      to: 2,
      rates: Object.freeze([91, 123.2, 115.08, 136.64, 137.48, 141.4, 179.76, 230.44, 345.8]),
      step: Object.freeze([19.6, 28.28, 30.52, 29.96, 43.12, 46.2, 46.76, 60.48, 77]),
    }),
    Object.freeze({
      from: 2.5,
      to: 5,
      rates: Object.freeze([169.4, 236.32, 237.16, 257.04, 309.96, 325.92, 366.8, 472.36, 653.8]),
      step: Object.freeze([19.04, 28, 28, 29.12, 41.16, 44.52, 49.56, 60.48, 76.72]),
    }),
    Object.freeze({
      from: 5,
      to: 10,
      rates: Object.freeze([264.6, 376.32, 377.16, 402.64, 515.76, 548.52, 614.6, 774.76, 1037.4]),
      step: Object.freeze([17.92, 26.6, 27.44, 28.84, 37.24, 44.52, 44.8, 54.32, 69.16]),
    }),
    Object.freeze({
      from: 10,
      to: 20,
      rates: Object.freeze([443.8, 642.32, 651.56, 691.04, 888.16, 993.72, 1062.6, 1317.96, 1729]),
      step: Object.freeze([14.56, 22.96, 25.76, 28.56, 34.72, 42, 39.76, 47.6, 63.56]),
    }),
    Object.freeze({
      from: 20.5,
      to: 30,
      rates: Object.freeze([346.19, 443.08, 510.36, 597.87, 1097.82, 1130.84, 1015.95, 1075.62, 1533.56]),
      step: Object.freeze([4.94, 10.34, 10.32, 11.83, 23.94, 17.51, 20.7, 21.71, 33.46]),
    }),
  ]);

  const PER_KG_SEGMENTS = Object.freeze([
    Object.freeze({ to: 70, rates: Object.freeze([15.4, 18.9, 21.01, 24.72, 47.52, 50.22, 42.56, 44.4, 70]) }),
    Object.freeze({ to: 300, rates: Object.freeze([17.6, 22.68, 24.83, 28.84, 50.16, 53.01, 45.6, 51.8, 80]) }),
    Object.freeze({ to: Number.POSITIVE_INFINITY, rates: Object.freeze([19.04, 25.2, 27.43, 29.64, 52.92, 55.98, 47.6, 52.91, 82.8]) }),
  ]);

  const round = (value, digits = 2) => {
    const scale = 10 ** digits;
    return Math.round((value + Number.EPSILON) * scale) / scale;
  };

  const roundUpHalf = (value) => Math.ceil(value * 2) / 2;

  function rateFor(weightKg, zone) {
    const weight = Number(weightKg);
    const zoneIndex = Number(zone) - 1;
    if (!(weight > 0) || !Number.isInteger(zoneIndex) || zoneIndex < 0 || zoneIndex > 8) return null;

    if (weight <= 30) {
      const roundedWeight = Math.max(0.5, roundUpHalf(weight));
      const segment = HALF_KG_SEGMENTS.find(({ from, to }) => roundedWeight >= from && roundedWeight <= to);
      if (!segment) return null;
      const halfKgSteps = Math.round((roundedWeight - segment.from) * 2);
      return round(segment.rates[zoneIndex] + segment.step[zoneIndex] * halfKgSteps);
    }

    const roundedWeight = Math.ceil(weight);
    const segment = PER_KG_SEGMENTS.find(({ to }) => weight <= to);
    return round(segment.rates[zoneIndex] * roundedWeight);
  }

  function estimate({
    widthCm,
    heightCm,
    zone,
    packageExtraCm = 15,
    cartonWidthCm = 22,
    cartonHeightCm = 18,
    baseWeightKg = 2.5,
    perimeterKgPerM = 0.6,
    areaKgPerM2 = 0.8,
    fuelPercent = 30,
    bufferPercent = 10,
    cnyToStoreRate = 1.08,
  } = {}) {
    const width = Number(widthCm);
    const height = Number(heightCm);
    const destinationZone = Number(zone);

    if (!(width > 0 && height > 0)) {
      return Object.freeze({
        outerLengthCm: null,
        cartonWidthCm: null,
        cartonHeightCm: null,
        volumetricWeightKg: null,
        actualWeightKg: null,
        billableWeightKg: null,
        baseFreightCny: null,
        estimatedFreightCny: null,
        estimatedStoreCurrency: null,
        manualQuote: false,
      });
    }

    const extra = Math.max(0, Number(packageExtraCm) || 0);
    const boxWidth = Math.max(0, Number(cartonWidthCm) || 0);
    const boxHeight = Math.max(0, Number(cartonHeightCm) || 0);
    const longestSide = Math.max(width, height);
    const packageCount = Math.max(1, Math.ceil((longestSide + extra) / 300));
    const segmentWidth = width >= height ? width / packageCount : width;
    const segmentHeight = height > width ? height / packageCount : height;
    const outerLength = round(Math.max(segmentWidth, segmentHeight) + extra, 1);
    const areaM2 = (width * height) / 10000;
    const segmentAreaM2 = (segmentWidth * segmentHeight) / 10000;
    const segmentPerimeterM = (2 * segmentWidth + 2 * segmentHeight) / 100;
    const packageActualWeight = round(
      Math.max(0, Number(baseWeightKg) || 0)
        + segmentPerimeterM * Math.max(0, Number(perimeterKgPerM) || 0)
        + segmentAreaM2 * Math.max(0, Number(areaKgPerM2) || 0),
      3,
    );
    const packageVolumetricWeight = round((outerLength * boxWidth * boxHeight) / 5000, 3);
    const packageBillableWeight = roundUpHalf(Math.max(packageActualWeight, packageVolumetricWeight));
    const packageBaseFreight = rateFor(packageBillableWeight, destinationZone);
    const actualWeight = round(packageActualWeight * packageCount, 3);
    const volumetricWeight = round(packageVolumetricWeight * packageCount, 3);
    const billableWeight = round(packageBillableWeight * packageCount, 3);
    const manualQuote = packageBaseFreight === null;
    const baseFreight = manualQuote ? null : round(packageBaseFreight * packageCount);
    const fuelMultiplier = 1 + Math.max(0, Number(fuelPercent) || 0) / 100;
    const bufferMultiplier = 1 + Math.max(0, Number(bufferPercent) || 0) / 100;
    const exchangeMultiplier = Math.max(0, Number(cnyToStoreRate) || 0);
    const estimatedFreight = baseFreight === null ? null : round(baseFreight * fuelMultiplier * bufferMultiplier);

    return Object.freeze({
      outerLengthCm: outerLength,
      packageCount,
      cartonWidthCm: boxWidth,
      cartonHeightCm: boxHeight,
      volumetricWeightKg: volumetricWeight,
      actualWeightKg: actualWeight,
      billableWeightKg: billableWeight,
      baseFreightCny: baseFreight,
      estimatedFreightCny: estimatedFreight,
      estimatedStoreCurrency: estimatedFreight === null ? null : round(estimatedFreight * exchangeMultiplier),
      manualQuote,
    });
  }

  global.HoneycombDhlEstimator = Object.freeze({ estimate, rateFor, roundUpHalf });
})(globalThis);
